/* eslint-disable no-useless-escape */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Rate from "antd/lib/rate";
import "antd/lib/rate/style";
import Popover from "antd/lib/popover";
import "antd/lib/popover/style";
import Spin from "antd/lib/spin";
import "antd/lib/spin/style";
import { RateAssetForm } from "@/services/service-directory";
import SocialSharing from "@/components/SocialSharing";
import { useLocation, navigate, Link } from "@reach/router";
import { RootState } from "@/redux/reducers";
import { selectApiDocBySlug } from "@/redux/selectors/api-docs";
import {
  rateAssetActions,
  fetchAssetSocialActions,
  uiActions,
} from "@/redux/actions";
import "./styles.less";
import iconShareSquare from "@/assets/icons/share-square.svg";
import SocialPopup from "@/containers/SocialPopup";
import authHelpers from "@/services/auth/helpers";
import { AssetSocial } from "@/types";
import isEmpty from "lodash/isEmpty";
import env from "@/env";
import { getSubdomain } from "@/utils";

interface LevelCrumb {
  title: string;
  description?: string;
  crumb: {
    label: string;
    url: string;
  };
  regex?: RegExp | string;
}

const pluralOrSingular = (num: number) => {
  return num <= 1 ? "person" : "people";
};

const checkIsDetailPage = (pathname: string) => {
  return pathname.split("/").length === 4 && pathname.split("/")[1] === "apis";
};

const getLikeText = (liked: boolean) => {
  return {
    className: liked ? "liked" : "not-like",
    text: liked ? "Liked" : "Like",
  };
};

const getParams = (pathname: string, hash: string) => {
  const isDetailPage = checkIsDetailPage(pathname);
  return {
    group: isDetailPage ? pathname.split("/")[2].replace(hash, "") : "",
    api: isDetailPage ? pathname.split("/")[3].replace(hash, "") : "",
  };
};

const getInitialRate = (
  isDetailPage: boolean,
  apiDoc: any,
  organisation: any
): number => {
  if (isDetailPage) {
    return apiDoc?.rating ?? 0;
  }
  return organisation?.rating ?? 0;
};
const EntitySocialVote = () => {
  const dispatch = useDispatch();
  const organisation = useSelector(
    (state: RootState) => state.site.organisation
  );
  const assetsSocial = useSelector(
    (state: RootState) => state.openBankingDiretory.assetsSocial
  );
  const { selectedGroupApiHome } = useSelector((state: RootState) => state.ui);
  const { pathname, hash } = useLocation();
  const isFetchingSocial = useSelector(
    (state: RootState) => state.loading.FETCH_ASSET_SOCIAL
  );
  const isDetailPage = checkIsDetailPage(pathname);

  const [openPopupSocial, setOpenPopupSocial] = useState<boolean>(false);
  const params = getParams(pathname, hash);
  const socialUserToken = authHelpers.getSocialToken();
  const apiDoc = useSelector((state: RootState) =>
    selectApiDocBySlug(state, params.group, params.api)
  );
  const [breadCrumbs, setBreadCrumbs] = useState<
    { label: string; url: string }[]
  >([]);
  const initRating = getInitialRate(isDetailPage, apiDoc, organisation);
  const [rate, setRate] = useState<{ thankMessage: string; value: number }>({
    thankMessage: "",
    value: initRating,
  });
  const [secondLevelCrumb, setSecondLevelCrumb] = useState<LevelCrumb>({
    title: "",
    description: "",
    crumb: {
      label: "",
      url: "",
    },
    regex: "",
  });

  const organisationNumRaters = organisation?.numRaters ?? 0;
  const apiDocNumRaters = apiDoc?.numRaters ?? 0;

  const secondLevelsInfo = [
    {
      title: "API Documentation",
      description: organisation?.description,
      crumb: {
        label: "APIs",
        url: "/apis",
      },
      regex: /[\/]apis/g,
    },
    {
      title: "Showcase",
      description: "",
      crumb: {
        label: "Showcase",
        url: "/showcase",
      },
      regex: /[\/]showcase/g,
    },
    {
      title: organisation?.displayName,
      description: organisation?.description,
      crumb: {
        label: "Overview",
        url: "/overview",
      },
      regex: /[\/]overview/g,
    },
  ];

  const [liked, setLike] = useState<boolean>(false);

  const handleClickCrumb = (url: string, label?: string) => {
    if (label && label === "APIs") {
      // When user click Apis will be reset the action persistSelectedGroupApiHome to empty
      dispatch(uiActions.persistSelectedGroupApiHome("", { x: 0, y: 0 }));
    }
    url && pathname !== url && navigate(url);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRate = (rating: number) => {
    const social_user = authHelpers.getSocialToken();
    if (!social_user) {
      toggleModal();
      const assetHistory: RateAssetForm = {
        rateType: "Rate",
        ratedAsset: apiDoc ? "Api" : "Entity",
        ratedId: apiDoc ? apiDoc.id : organisation.id,
        ratedScore: rating,
      };
      authHelpers.storeAssetHistory(JSON.stringify(assetHistory));
    } else {
      // Rate asset
      const rateObject: RateAssetForm = {
        rateType: "Rate",
        ratedAsset: apiDoc ? "Api" : "Entity",
        ratedId: apiDoc ? apiDoc.id : organisation.id,
        ratedScore: rating,
      };
      dispatch(rateAssetActions.request(rateObject, social_user));
      setRate({
        value: rating,
        thankMessage:
          rating !== 0
            ? "Thank you for rating!"
            : apiDoc
            ? "Rate this Api!"
            : "Rate this Entity!",
      });
    }
  };

  const handleLike = () => {
    const social_user = authHelpers.getSocialToken();
    if (!social_user) {
      toggleModal();
      const assetHistory: RateAssetForm = {
        rateType: "Like",
        ratedAsset: apiDoc ? "Api" : "Entity",
        ratedId: apiDoc ? apiDoc.id : organisation.id,
        ratedScore: liked ? 0 : 1,
      };
      authHelpers.storeAssetHistory(JSON.stringify(assetHistory));
    } else {
      // Like asset
      const rateObject: RateAssetForm = {
        rateType: "Like",
        ratedAsset: apiDoc ? "Api" : "Entity",
        ratedId: apiDoc ? apiDoc.id : organisation.id,
        ratedScore: liked ? 0 : 1,
      };
      dispatch(rateAssetActions.request(rateObject, social_user));
      setLike(!liked);
    }
  };

  const toggleModal = () => {
    setOpenPopupSocial(!openPopupSocial);
  };

  useEffect(() => {
    if (socialUserToken) {
      // Dispatch action Rate or Like asset after Login Success:
      const assetHistory: RateAssetForm = authHelpers.getAssetHistory();
      if (assetHistory) {
        assetHistory?.rateType === "Like" &&
          setLike(!!assetHistory?.ratedScore);
        assetHistory?.rateType === "Rate" &&
          setRate({
            value: assetHistory.ratedScore,
            thankMessage:
              assetHistory.ratedScore !== 0
                ? "Thank you for rating!"
                : apiDoc
                ? "Rate this Api!"
                : "Rate this Entity!",
          });
        authHelpers.clearAssetHistory();
        dispatch(rateAssetActions.request(assetHistory, socialUserToken));
      }
      dispatch(fetchAssetSocialActions.request(socialUserToken));
    }
  }, [dispatch, socialUserToken, apiDoc]);

  useEffect(() => {
    if (organisation) {
      const firstCrumb = {
        // label: organisation?.displayName,
        label: "Home",
        url: "/",
      };
      const secondCrumb = secondLevelsInfo.find((level) =>
        level.regex.test(pathname)
      );
      let crumbs = secondCrumb
        ? [firstCrumb, secondCrumb?.crumb]
        : [firstCrumb];
      secondCrumb &&
        secondCrumb?.crumb.label === "APIs" &&
        selectedGroupApiHome.name &&
        (crumbs = [
          ...crumbs,
          { label: selectedGroupApiHome.name, url: "/apis" },
        ]);
      secondCrumb && setSecondLevelCrumb(secondCrumb);
      if (isDetailPage && apiDoc) {
        const apiDocCrumb = {
          label: apiDoc.apiName,
          url: "",
        };
        selectedGroupApiHome.name
          ? setBreadCrumbs([...crumbs, apiDocCrumb])
          : setBreadCrumbs([
              ...crumbs,
              { label: apiDoc.groupName, url: "/apis" },
              apiDocCrumb,
            ]);
      } else {
        setBreadCrumbs(crumbs);
      }
    }
    // eslint-disable-next-line
  }, [apiDoc, isDetailPage, organisation, pathname, selectedGroupApiHome]);
  useEffect(() => {
    if (!assetsSocial || !socialUserToken) return;

    const findAsset = (
      rateType: string,
      ratedAsset: string,
      ratedId?: string
    ) =>
      assetsSocial.find(
        (asset: AssetSocial) =>
          asset.rateType === rateType &&
          asset.ratedAsset === ratedAsset &&
          asset.ratedId === ratedId
      );

    const handleRatingAndLike = (
      ratedAsset: "Api" | "Entity",
      ratedId?: string,
      isApiDoc?: boolean
    ) => {
      const rating = findAsset("Rate", ratedAsset, ratedId);
      const like = findAsset("Like", ratedAsset, ratedId);

      if (!rating) setRate({ thankMessage: "", value: 0 });
      else setRate({ thankMessage: "", value: rating.ratedScore });

      if (!like) setLike(false);
      else setLike(like.ratedScore === 1);
    };

    if (apiDoc) {
      handleRatingAndLike("Api", apiDoc.id, true);
    } else if (organisation) {
      handleRatingAndLike("Entity", organisation.id, false);
    }

    setRate((prev) => ({ ...prev, value: initRating }));
  }, [
    pathname,
    assetsSocial,
    socialUserToken,
    apiDoc,
    organisation,
    initRating,
  ]);

  return (
    <div className="EntitySocialVote">
      <div className="EntitySocialVote-container">
        <div className="EntitySocialVote-wrapper">
          <div className="EntitySocialVote-info">
            <h1 className="EntitySocialVote-info-title">
              {secondLevelCrumb?.title || organisation?.displayName}
            </h1>
          </div>
          <div className="EntitySocialVote-social">
            {getSubdomain() !== env.app.tymeSubDomain && (
              <>
                <div className="EntitySocialVote-social-share">
                  <Popover
                    placement="bottom"
                    content={
                      <SocialSharing
                        api={{
                          linkId: undefined,
                          summary: apiDoc?.description ?? "",
                        }}
                      />
                    }
                  >
                    <img src={iconShareSquare} alt="Open Banking" />
                  </Popover>
                </div>
                <Spin
                  spinning={!!isFetchingSocial && !isEmpty(socialUserToken)}
                >
                  <div className="EntitySocialVote-social-like">
                    <div
                      onClick={handleLike}
                      className="EntitySocialVote-social-like-container"
                      data-testid="like-button"
                      aria-label="Like"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleLike();
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="43"
                        height="45.867"
                        viewBox="0 0 43 45.867"
                      >
                        <path
                          className={`${getLikeText(liked).className}`}
                          id="thumbs-up"
                          d="M41.77 25.683A8.471 8.471 0 0 0 43 21.218a7.8 7.8 0 0 0-7.688-7.667h-3.268a11.438 11.438 0 0 0 .793-4.169C32.837 2.861 29.46 0 24.3 0c-5.519 0-5.2 8.5-6.429 9.729-2.038 2.038-4.445 5.953-6.16 7.471H2.867A2.867 2.867 0 0 0 0 20.067v21.5a2.867 2.867 0 0 0 2.867 2.867H8.6a2.868 2.868 0 0 0 2.775-2.146c3.987.09 6.724 3.578 15.928 3.578h1.991c6.908 0 10.032-3.532 10.118-8.54a8.637 8.637 0 0 0 1.553-6 8.32 8.32 0 0 0 .805-5.643zM36.238 30.5a3.978 3.978 0 0 1-1.249 5.157c.69 4.37-1.577 5.9-4.759 5.9h-3.387c-6.418 0-10.573-3.388-15.376-3.388V21.5h.978c2.541 0 6.09-6.351 8.469-8.731 2.541-2.54 1.694-6.775 3.386-8.469 4.235 0 4.235 2.954 4.235 5.082 0 3.509-2.541 5.081-2.541 8.469h9.316a3.5 3.5 0 0 1 3.39 3.388c.008 1.693-1.148 3.387-2 3.387a4.3 4.3 0 0 1-.467 5.878zM7.883 38.7a2.15 2.15 0 1 1-2.15-2.15 2.15 2.15 0 0 1 2.15 2.15z"
                        />
                      </svg>
                      <span className={`text ${getLikeText(liked).className}`}>
                        {getLikeText(liked).text}
                      </span>
                    </div>
                  </div>
                </Spin>
                <Spin
                  spinning={!!isFetchingSocial && !isEmpty(socialUserToken)}
                >
                  <div className="EntitySocialVote-social-rating">
                    <Rate
                      className="EntitySocialVote-social-rating-stars"
                      allowHalf
                      defaultValue={0}
                      value={rate.value}
                      data-testid="rate-stars"
                      onChange={handleRate}
                    />

                    <div className="EntitySocialVote-social-rating-numbers">
                      {rate.thankMessage ||
                        (isDetailPage
                          ? apiDoc && apiDoc?.rating > 0
                            ? `${apiDoc?.rating} ${
                                apiDocNumRaters
                                  ? `By ${apiDocNumRaters} ${pluralOrSingular(
                                      apiDocNumRaters
                                    )}`
                                  : ""
                              }`
                            : "Be the first to rate this API!"
                          : organisation && organisation?.rating > 0
                          ? `${organisation?.rating} ${
                              organisationNumRaters
                                ? `By ${organisationNumRaters} ${pluralOrSingular(
                                    organisationNumRaters
                                  )}`
                                : ""
                            }`
                          : "Be the first to rate this API!")}
                    </div>
                  </div>
                </Spin>
              </>
            )}
          </div>
        </div>
        <div className="EntitySocialVote-breadCrumbs">
          {breadCrumbs.map((crumb, index) => (
            <Link key={index} to={crumb.url}>
              <div
                key={crumb.label}
                className="EntitySocialVote-breadCrumbs-crumb"
                onClick={() => handleClickCrumb(crumb.url, crumb.label)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleClickCrumb(crumb.url, crumb.label);
                  }
                }}
              >
                <span className="EntitySocialVote-breadCrumbs-crumb-label">
                  {crumb.label}
                </span>
                {index !== breadCrumbs.length - 1 && (
                  <span className="EntitySocialVote-breadCrumbs-crumb-slash">
                    /
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
      <SocialPopup active={openPopupSocial} toggleModal={toggleModal} />
    </div>
  );
};

export default EntitySocialVote;
